import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import {
  Autocomplete,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import RecyclingIcon from '@mui/icons-material/Recycling';
import PendingIcon from '@mui/icons-material/Pending';
import RouteIcon from '@mui/icons-material/Route';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhotoIcon from '@mui/icons-material/Photo';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { gestionAPI, serviciosAPI, zonasAPI, contribuyentesAPI, movilesAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';

const FORM_EMPTY = {
  Servicio: '',
  Zona: '',
  Nombre: '',
  Email: '',
  Latitud: '',
  Longitud: '',
  FechaPedido: '',
  Volumen: '',
  Estado: 'pendiente',
  FechaRetiro: '',
  MovilRetiro: '',
};

function toDatetimeLocal(d) {
  if (!d) return '';
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

const ESTADOS = [
  { value: '', label: 'Todos' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'con_itinerario', label: 'Con itinerario' },
  { value: 'recolectada', label: 'Recolectada' },
  { value: 'anulada', label: 'Anulada' },
];

const ESTADO_CONFIG = {
  pendiente:       { label: 'Pendiente',       color: 'warning', icon: <PendingIcon fontSize="small" /> },
  con_itinerario:  { label: 'Con itinerario',   color: 'info',    icon: <RouteIcon fontSize="small" /> },
  recolectada:     { label: 'Recolectada',      color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  anulada:         { label: 'Anulada',          color: 'error',   icon: <CancelIcon fontSize="small" /> },
};

function ResumenCard({ label, value, color, icon, active, onClick }) {
  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2,
        cursor: 'pointer',
        border: active ? 2 : 1,
        borderColor: active ? `${color}.main` : 'divider',
        bgcolor: active ? `${color}.50` : 'background.paper',
        transition: 'all .15s',
        '&:hover': { borderColor: `${color}.main`, bgcolor: `${color}.50` },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={{ color: `${color}.main` }}>{icon}</Box>
        <Typography variant="h5" fontWeight={700}>{value ?? '…'}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

export default function Recolecciones() {
  const queryClient = useQueryClient();
  const organizacion = useAuthStore((state) => state.user?.organizacion);

  const location = useLocation();
  const navState = location.state ?? {};

  const hasta = navState.fecha ?? format(new Date(), 'yyyy-MM-dd');
  const desdeDefault = navState.fecha
    ? format(subDays(new Date(navState.fecha + 'T00:00:00'), 365), 'yyyy-MM-dd')
    : format(subDays(new Date(), 30), 'yyyy-MM-dd');

  const [fechaDesde, setFechaDesde] = useState(desdeDefault);
  const [fechaHasta, setFechaHasta] = useState(hasta);
  const [servicio, setServicio] = useState(navState.servicio ?? '');
  const [estado, setEstado] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmAnular, setConfirmAnular] = useState({ open: false, rec: null });
  const [fotoDialog, setFotoDialog] = useState({ open: false, foto: null, nombre: '' });
  const [editDialog, setEditDialog] = useState({ open: false, rec: null }); // rec=null → crear
  const [form, setForm] = useState(FORM_EMPTY);
  const [busquedaInput, setBusquedaInput] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const queryKey = ['gestion-recolecciones', fechaDesde, fechaHasta, servicio, estado, page, rowsPerPage];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () =>
      gestionAPI.listarRecolecciones({
        fechaDesde,
        fechaHasta,
        ...(servicio && { servicio }),
        ...(estado && { estado }),
        page: page + 1,
        limit: rowsPerPage,
      }).then((r) => r.data),
    keepPreviousData: true,
  });

  const { data: servicios } = useQuery({
    queryKey: ['servicios'],
    queryFn: () => serviciosAPI.listar().then((r) => r.data.data),
  });

  // Zonas/servicios que contienen la ubicación del contribuyente seleccionado
  const latOk = form.Latitud !== '' && form.Latitud != null && !isNaN(parseFloat(form.Latitud));
  const lngOk = form.Longitud !== '' && form.Longitud != null && !isNaN(parseFloat(form.Longitud));

  const { data: zonasPorUbicacion, isFetching: buscandoZonas } = useQuery({
    queryKey: ['zonas-por-ubicacion', form.Latitud, form.Longitud],
    queryFn: () =>
      gestionAPI.zonasPorUbicacion(form.Latitud, form.Longitud).then((r) => r.data.data),
    enabled: !!(editDialog.open && !editDialog.rec && latOk && lngOk),
    keepPreviousData: false,
  });

  // Servicios únicos para la ubicación (crear) o todos (editar)
  const serviciosForm = (!editDialog.rec && zonasPorUbicacion)
    ? [...new Map(zonasPorUbicacion.map((z) => [z.IdServicio, z])).values()]
    : (servicios ?? []).map((s) => ({ IdServicio: s.IdServicio, NombreServicio: s.NombreServicio }));

  // En editar necesitamos todas las zonas de la org
  const { data: todasZonas } = useQuery({
    queryKey: ['zonas'],
    queryFn: () => zonasAPI.listar().then((r) => r.data.data),
    enabled: !!editDialog.rec,
  });

  const { data: moviles } = useQuery({
    queryKey: ['moviles'],
    queryFn: () => movilesAPI.listar().then((r) => r.data.data),
    enabled: !!editDialog.rec,
  });

  // Debounce de la búsqueda de contribuyentes
  useEffect(() => {
    const t = setTimeout(() => setBusqueda(busquedaInput), 300);
    return () => clearTimeout(t);
  }, [busquedaInput]);

  const { data: resultadosBusqueda, isFetching: buscando } = useQuery({
    queryKey: ['contribuyentes-buscar', busqueda],
    queryFn: () => contribuyentesAPI.buscar(busqueda).then((r) => r.data.data),
    enabled: busqueda.trim().length >= 2,
    keepPreviousData: false,
  });

  // Zonas: en crear filtradas por ubicación+servicio; en editar por servicio
  const zonasForm = editDialog.rec
    ? (todasZonas ?? []).filter((z) => !form.Servicio || z.IdServicio === parseInt(form.Servicio))
    : (zonasPorUbicacion ?? []).filter((z) => !form.Servicio || z.IdServicio === parseInt(form.Servicio));

  const openCrear = () => {
    setForm({ ...FORM_EMPTY, FechaPedido: toDatetimeLocal(new Date()) });
    setBusquedaInput('');
    setBusqueda('');
    setEditDialog({ open: true, rec: null });
  };

  const openEditar = (rec) => {
    setForm({
      Servicio: rec.Servicio,
      Zona: rec.Zona,
      Nombre: rec.Nombre,
      Email: rec.Email,
      Latitud: rec.Latitud,
      Longitud: rec.Longitud,
      FechaPedido: toDatetimeLocal(rec.FechaPedido),
      Volumen: rec.Volumen ?? '',
      Estado: rec.estado,
      FechaRetiro: toDatetimeLocal(rec.FechaRetiro),
      MovilRetiro: rec.MovilRetiro ?? '',
    });
    setEditDialog({ open: true, rec });
  };

  const crearMutation = useMutation({
    mutationFn: (data) => gestionAPI.crearRecoleccion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gestion-recolecciones'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-resumen'] });
      setEditDialog({ open: false, rec: null });
      setSnackbar({ open: true, message: 'Recolección creada', severity: 'success' });
    },
    onError: (err) => {
      setSnackbar({ open: true, message: err.response?.data?.error?.message || 'Error al crear', severity: 'error' });
    },
  });

  const actualizarMutation = useMutation({
    mutationFn: ({ id, data }) => gestionAPI.actualizarRecoleccion(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gestion-recolecciones'] });
      setEditDialog({ open: false, rec: null });
      setSnackbar({ open: true, message: 'Recolección actualizada', severity: 'success' });
    },
    onError: (err) => {
      setSnackbar({ open: true, message: err.response?.data?.error?.message || 'Error al actualizar', severity: 'error' });
    },
  });

  const handleGuardar = () => {
    const payload = {
      Servicio: form.Servicio,
      Zona: form.Zona,
      Nombre: form.Nombre.trim(),
      Email: form.Email.trim(),
      Latitud: form.Latitud,
      Longitud: form.Longitud,
      FechaPedido: form.FechaPedido,
      Volumen: form.Volumen || 0,
    };
    if (editDialog.rec) {
      payload.Estado = form.Estado;
      if (form.Estado === 'recolectada') payload.FechaRetiro = form.FechaRetiro || null;
      payload.MovilRetiro = form.MovilRetiro || null;
      actualizarMutation.mutate({ id: editDialog.rec.Id, data: payload });
    } else {
      crearMutation.mutate(payload);
    }
  };

  const formInvalid =
    !form.Servicio || !form.Zona || !form.Nombre || !form.Email ||
    !form.Latitud || !form.Longitud || !form.FechaPedido;

  const anularMutation = useMutation({
    mutationFn: (id) => gestionAPI.anularRecoleccion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gestion-recolecciones'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-resumen'] });
      setConfirmAnular({ open: false, rec: null });
      setSnackbar({ open: true, message: 'Recolección anulada', severity: 'success' });
    },
    onError: (err) => {
      setConfirmAnular({ open: false, rec: null });
      setSnackbar({ open: true, message: err.response?.data?.error?.message || 'Error al anular', severity: 'error' });
    },
  });

  const handleEstadoCard = useCallback((val) => {
    setEstado((prev) => (prev === val ? '' : val));
    setPage(0);
  }, []);

  const rows = data?.data ?? [];
  const resumen = data?.resumen ?? {};
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      {/* Encabezado */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <RecyclingIcon sx={{ fontSize: 32 }} />
        <Typography variant="h4" sx={{ flex: 1 }}>Recolecciones</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCrear}>
          Nueva recolección
        </Button>
      </Box>

      {/* Cards resumen — clickeables para filtrar */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <ResumenCard
            label="Pendientes"
            value={resumen.pendiente}
            color="warning"
            icon={<PendingIcon />}
            active={estado === 'pendiente'}
            onClick={() => handleEstadoCard('pendiente')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <ResumenCard
            label="Con itinerario"
            value={resumen.con_itinerario}
            color="info"
            icon={<RouteIcon />}
            active={estado === 'con_itinerario'}
            onClick={() => handleEstadoCard('con_itinerario')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <ResumenCard
            label="Recolectadas"
            value={resumen.recolectada}
            color="success"
            icon={<CheckCircleIcon />}
            active={estado === 'recolectada'}
            onClick={() => handleEstadoCard('recolectada')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <ResumenCard
            label="Anuladas"
            value={resumen.anulada}
            color="error"
            icon={<CancelIcon />}
            active={estado === 'anulada'}
            onClick={() => handleEstadoCard('anulada')}
          />
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Desde"
              size="small"
              value={fechaDesde}
              onChange={(e) => { setFechaDesde(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Hasta"
              size="small"
              value={fechaHasta}
              onChange={(e) => { setFechaHasta(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Servicio"
              size="small"
              value={servicio}
              onChange={(e) => { setServicio(e.target.value); setPage(0); }}
            >
              <MenuItem value="">Todos</MenuItem>
              {(servicios ?? []).map((s) => (
                <MenuItem key={s.Id} value={s.IdServicio}>{s.NombreServicio}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Estado"
              size="small"
              value={estado}
              onChange={(e) => { setEstado(e.target.value); setPage(0); }}
            >
              {ESTADOS.map((e) => (
                <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla */}
      <Paper>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {isError && (
          <Alert severity="error" sx={{ m: 2 }}>Error al cargar recolecciones</Alert>
        )}
        {!isLoading && !isError && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Servicio</TableCell>
                    <TableCell>Zona</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Alias</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Fecha pedido</TableCell>
                    <TableCell>Itinerario</TableCell>
                    <TableCell>Fecha retiro</TableCell>
                    <TableCell>Móvil</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No hay recolecciones para los filtros seleccionados
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((rec) => {
                      const est = ESTADO_CONFIG[rec.estado];
                      return (
                        <TableRow key={rec.Id} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{rec.Id}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {rec.ImagenServicio && (
                                <Box
                                  component="img"
                                  src={`/uploads/${organizacion}/${rec.ImagenServicio}`}
                                  alt=""
                                  sx={{ width: 20, height: 20, objectFit: 'contain' }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              {rec.NombreServicio}
                            </Box>
                          </TableCell>
                          <TableCell>{rec.NombreZona}</TableCell>
                          <TableCell>{rec.Nombre}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {rec.Alias ?? <span style={{ color: '#bbb' }}>—</span>}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{rec.Email}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                            {rec.FechaPedido
                              ? format(new Date(rec.FechaPedido), 'dd/MM/yyyy HH:mm')
                              : '—'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                            {rec.Lote ?? <span style={{ color: '#bbb' }}>—</span>}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                            {rec.FechaRetiro
                              ? format(new Date(rec.FechaRetiro), 'dd/MM/yyyy HH:mm')
                              : <span style={{ color: '#bbb' }}>—</span>}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                            {rec.DescripcionMovil ?? <span style={{ color: '#bbb' }}>—</span>}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={est.label}
                              color={est.color}
                              size="small"
                              icon={est.icon}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {rec.Foto && (
                              <Tooltip title="Ver foto">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => setFotoDialog({ open: true, foto: rec.Foto, nombre: rec.Nombre })}
                                >
                                  <PhotoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Ver en mapa">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  window.open(
                                    `https://www.google.com/maps?q=${rec.Latitud},${rec.Longitud}`,
                                    '_blank'
                                  )
                                }
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton size="small" color="info" onClick={() => openEditar(rec)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {rec.estado === 'pendiente' || rec.estado === 'con_itinerario' ? (
                              <Tooltip title="Anular">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setConfirmAnular({ open: true, rec })}
                                >
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Filas"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Paper>

      {/* Diálogo crear / editar recolección */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, rec: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editDialog.rec ? `Editar recolección #${editDialog.rec.Id}` : 'Nueva recolección'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>

            {/* 1 — Búsqueda de contribuyente (solo en modo crear) */}
            {!editDialog.rec && (
              <Grid item xs={12}>
                <Autocomplete
                  options={resultadosBusqueda ?? []}
                  getOptionLabel={(opt) =>
                    typeof opt === 'string' ? opt : `${opt.Nombre ?? ''} — ${opt.Email ?? ''}`
                  }
                  isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
                  filterOptions={(x) => x}
                  inputValue={busquedaInput}
                  onInputChange={(_, val) => setBusquedaInput(val)}
                  onChange={(_, opt) => {
                    if (opt) {
                      setForm((f) => ({
                        ...f,
                        Nombre: opt.Nombre ?? '',
                        Email: opt.Email ?? '',
                        Latitud: opt.Latitud ?? '',
                        Longitud: opt.Longitud ?? '',
                        Servicio: '',
                        Zona: '',
                      }));
                    }
                  }}
                  loading={buscando}
                  noOptionsText={
                    busqueda.trim().length < 2
                      ? 'Escribí nombre o email para buscar'
                      : 'Sin resultados'
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar contribuyente por nombre o email"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {buscando ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}

            {/* 2 — Datos del contribuyente (pre-llenados al seleccionar) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre"
                size="small"
                value={form.Nombre}
                onChange={(e) => setForm((f) => ({ ...f, Nombre: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                size="small"
                type="email"
                value={form.Email}
                onChange={(e) => setForm((f) => ({ ...f, Email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Latitud"
                size="small"
                type="number"
                inputProps={{ step: 'any' }}
                value={form.Latitud}
                onChange={(e) => setForm((f) => ({ ...f, Latitud: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Longitud"
                size="small"
                type="number"
                inputProps={{ step: 'any' }}
                value={form.Longitud}
                onChange={(e) => setForm((f) => ({ ...f, Longitud: e.target.value }))}
              />
            </Grid>

            {/* 3 — Servicio y Zona (solo disponibles una vez que se tiene la ubicación) */}
            <Grid item xs={12}>
              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">Servicio y zona</Typography>
              </Divider>
            </Grid>

            {/* Aviso cuando no hay ubicación aún (solo en crear) */}
            {!editDialog.rec && !latOk && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Seleccioná un contribuyente con ubicación para ver los servicios disponibles
                </Alert>
              </Grid>
            )}
            {/* Aviso cuando hay ubicación pero no hay zonas coincidentes */}
            {!editDialog.rec && latOk && lngOk && !buscandoZonas && zonasPorUbicacion?.length === 0 && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  La ubicación del contribuyente no corresponde a ninguna zona configurada
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Servicio"
                size="small"
                value={form.Servicio}
                disabled={!editDialog.rec && (!latOk || buscandoZonas || serviciosForm.length === 0)}
                onChange={(e) => setForm((f) => ({ ...f, Servicio: e.target.value, Zona: '' }))}
              >
                {buscandoZonas
                  ? <MenuItem disabled>Buscando zonas...</MenuItem>
                  : serviciosForm.map((s) => (
                    <MenuItem key={s.IdServicio} value={s.IdServicio}>{s.NombreServicio}</MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Zona"
                size="small"
                value={form.Zona}
                disabled={!form.Servicio || buscandoZonas}
                onChange={(e) => setForm((f) => ({ ...f, Zona: e.target.value }))}
              >
                {zonasForm.map((z) => (
                  <MenuItem key={z.IdServicio + '_' + z.IdZona} value={z.IdZona}>
                    {z.NombreZona}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* 4 — Fecha pedido y volumen */}
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Fecha y hora del pedido"
                size="small"
                type="datetime-local"
                value={form.FechaPedido}
                onChange={(e) => setForm((f) => ({ ...f, FechaPedido: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Volumen"
                size="small"
                type="number"
                inputProps={{ min: 0, step: 'any' }}
                value={form.Volumen}
                onChange={(e) => setForm((f) => ({ ...f, Volumen: e.target.value }))}
              />
            </Grid>

            {/* 5 — Estado y fecha de recolección (solo en editar) */}
            {editDialog.rec && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">
                    <Typography variant="caption" color="text.secondary">Estado</Typography>
                  </Divider>
                </Grid>
                <Grid item xs={12} sm={form.Estado === 'recolectada' ? 4 : 12}>
                  <TextField
                    fullWidth
                    select
                    label="Estado"
                    size="small"
                    value={form.Estado}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        Estado: e.target.value,
                        FechaRetiro: e.target.value === 'recolectada' ? (f.FechaRetiro || toDatetimeLocal(new Date())) : '',
                      }))
                    }
                  >
                    <MenuItem value="pendiente">Pendiente</MenuItem>
                    <MenuItem value="con_itinerario" disabled>Con itinerario</MenuItem>
                    <MenuItem value="recolectada">Recolectada</MenuItem>
                    <MenuItem value="anulada">Anulada</MenuItem>
                  </TextField>
                </Grid>
                {form.Estado === 'recolectada' && (
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Fecha de recolección"
                      size="small"
                      type="datetime-local"
                      value={form.FechaRetiro}
                      onChange={(e) => setForm((f) => ({ ...f, FechaRetiro: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={form.Estado === 'recolectada' ? 12 : 6}>
                  <TextField
                    fullWidth
                    select
                    label="Móvil"
                    size="small"
                    value={form.MovilRetiro}
                    onChange={(e) => setForm((f) => ({ ...f, MovilRetiro: e.target.value }))}
                  >
                    <MenuItem value="">Sin asignar</MenuItem>
                    {(moviles ?? []).map((m) => (
                      <MenuItem key={m.IdMovil} value={m.IdMovil}>
                        {m.Descripcion} — {m.Dominio}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, rec: null })}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={formInvalid || crearMutation.isLoading || actualizarMutation.isLoading}
            onClick={handleGuardar}
          >
            {crearMutation.isLoading || actualizarMutation.isLoading ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo foto */}
      <Dialog
        open={fotoDialog.open}
        onClose={() => setFotoDialog({ open: false, foto: null, nombre: '' })}
        maxWidth="md"
      >
        <DialogTitle>
          Foto — {fotoDialog.nombre}
        </DialogTitle>
        <DialogContent sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
          <Box
            component="img"
            src={fotoDialog.foto}
            alt="Foto de recolección"
            sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 1 }}
            onError={(e) => { e.target.src = ''; e.target.alt = 'No se pudo cargar la imagen'; }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            href={fotoDialog.foto}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon />}
          >
            Abrir en nueva pestaña
          </Button>
          <Button onClick={() => setFotoDialog({ open: false, foto: null, nombre: '' })}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo confirmar anulación */}
      <Dialog open={confirmAnular.open} onClose={() => setConfirmAnular({ open: false, rec: null })}>
        <DialogTitle>Confirmar anulación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Anular la recolección de <strong>{confirmAnular.rec?.Nombre}</strong>?
          </Typography>
          {confirmAnular.rec?.Lote && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Esta recolección tiene itinerario asignado (<strong>{confirmAnular.rec.Lote}</strong>).
              Al anularla quedará pendiente en el itinerario.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAnular({ open: false, rec: null })}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={anularMutation.isLoading}
            onClick={() => anularMutation.mutate(confirmAnular.rec.Id)}
          >
            {anularMutation.isLoading ? 'Anulando…' : 'Anular'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
